
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { documentStore, uiStore } from "@/store/store";
import { DragPayload } from "@/lib/structure-panel-drag/util";
import { TreeNode } from "@/types/tree";
import {
  getNodeIdAtPointInCanvas,
  shouldItBeInsertedBeforeOrAfter,
  getStackingStyle,
  getNodesIdsAtPointInCanvas
} from "@/lib/canvas/drag/element-drag";
import {
  clearTextSelection,
  ElementPositionAndSize,
  getTreeNodePositionAndSize
} from "@/lib/canvas/helpers";
import { distance } from "@/util/distance";
import { CanvasDragPlaceholderPosition } from "@/types/ui";
import { findNode, getNodeParent, treeNodeReduce } from "@/lib/tree/tree";
import {
  findNearestParentThatWillAcceptNodes,
  getNestingRuleFromNode,
  isThisNodeAllowedInsideThatNode
} from "@/lib/tree/nesting";
import { EasyEvent, isCanvasEvent } from "@/util/easy-event";

type PlaceholderPosition = {
  dropTargetId: number;
  dragPlaceholderIndex: number;
  beforeOrAfter: "before" | "after";
}; // this is a silly type because we already have a very similar one - CanvasDragPlaceholderPosition

@Component
export default class OnCanvasDragHandle extends Vue {
  @Prop()
  node!: TreeNode;

  lastSubmittedDrag: false | DragPayload = false;

  get nestingPermissions() {
    return getNestingRuleFromNode(this.node);
  }

  get canvasElementDraggedId() {
    return uiStore.currentlyDraggingCanvasElementId;
  }

  @Watch("canvasElementDraggedId")
  watchCanvasElementDraggedId() {
    this.dragStart();
  }

  // Call this instead of dragStart directly. dragStart will be called by the watcher
  // This allows us to trigger a drag event from anywhere by updating the canvasElementDraggedId
  updateCurrentlyDraggingElementId() {
    uiStore.setCanvasElementDraggedId(this.node.id);
  }

  easyEvent = new EasyEvent();

  dragStart() {
    if (!uiStore.hasFullAccess || !this.canvasElementDraggedId) return;

    this.easyEvent.on("mousemove", this.dragging);
    this.easyEvent.on("mouseup", this.dragEnd);

    const maybeParent = getNodeParent(
      documentStore.document.tree,
      this.node.id
    );

    if (maybeParent) {
      this.setCanvasDragPlaceholderPosition({
        nodeIndex: -1, // bs, doesnt matter
        position: "before", // bs, doesnt matter
        parentId: maybeParent.id,
        onlyShowTargetOutline: true
      }); // only reason we call this is to outline the parent
    }

    clearTextSelection();
  }

  setCanvasDragPlaceholderPosition(
    canvasDragPlaceholderPosition: CanvasDragPlaceholderPosition | null
  ) {
    uiStore.setCanvasDragPlaceholderPosition(canvasDragPlaceholderPosition);
  }

  dragging(e: MouseEvent) {
    if (isCanvasEvent(e)) {
      e.preventDefault();
      e.stopPropagation();

      const mouseX = e.clientX;
      const mouseY = e.clientY;

      const nodeMouseIsOverId = getNodeIdAtPointInCanvas(mouseX, mouseY);

      if (nodeMouseIsOverId === undefined) return;

      const nodeMouseIsOver = findNode(
        documentStore.document.tree,
        nodeMouseIsOverId
      );
      if (nodeMouseIsOver === undefined) return;

      const dropTargetNode = findNearestParentThatWillAcceptNodes(
        documentStore.document.tree,
        nodeMouseIsOver,
        this.nestingPermissions
      );

      if (!dropTargetNode) return;

      const dropTargetNodeId = dropTargetNode.id;

      // ---------------------------------

      if (
        nodeMouseIsOverId === this.node.id ||
        treeNodeReduce(
          (node, foundNode: TreeNode | undefined) => {
            if (!foundNode) {
              foundNode = node.children.find(n => nodeMouseIsOverId === n.id);
            }
            return foundNode;
          },
          this.node,
          undefined
        )
      ) {
        /**
         * if over self, outline parent and do nothing
         */
        this.setCanvasDragPlaceholderPosition({
          nodeIndex: -1, // bs, doesnt matter
          position: "before", // bs, doesnt matter
          parentId: dropTargetNodeId,
          onlyShowTargetOutline: true
        }); // only reason we call this is to outline the parent

        this.setDropPosition(false);
      } else if (nodeMouseIsOverId === dropTargetNodeId) {
        /*
        if we are over the drop target, but not any of its children
        */
        this.handleDragWhenMouseIsOverTheDropTargetButNotOverAChildElementOfTheDropTarget(
          mouseX,
          mouseY,
          nodeMouseIsOverId,
          this.node.id,
          dropTargetNode
        );
      } else if (
        dropTargetNode.children.find(n => nodeMouseIsOverId === n.id)
      ) {
        /*
        if we are over an immediate child of the drop target, and not the self
        */
        this.handleDragWhenMouseIsOverAChildOfTheDropTarget(
          mouseX,
          mouseY,
          nodeMouseIsOverId,
          this.node.id,
          dropTargetNode
        );
      } else if (
        /*
        if we are over a deeper child of the drop target, and not the self
        */
        treeNodeReduce(
          (node, foundNode: TreeNode | undefined) => {
            if (!foundNode) {
              foundNode = node.children.find(n => nodeMouseIsOverId === n.id);
            }
            return foundNode;
          },
          dropTargetNode,
          undefined
        )
      ) {
        const childOfDropTargetThatMouseIsReallyOver =
          getNodesIdsAtPointInCanvas(mouseX, mouseY).reduce(
            (foundNode: TreeNode | undefined, nodeId: number) => {
              if (!foundNode) {
                return dropTargetNode.children.find(n => n.id === nodeId);
              }
              return foundNode;
            },
            undefined
          );

        if (childOfDropTargetThatMouseIsReallyOver) {
          this.handleDragWhenMouseIsOverAChildOfTheDropTarget(
            mouseX,
            mouseY,
            childOfDropTargetThatMouseIsReallyOver.id,
            this.node.id,
            dropTargetNode
          );
        }
      }
    }
  }

  dragEnd(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    this.easyEvent.removeAll();

    if (this.lastSubmittedDrag) {
      documentStore.moveElement(this.lastSubmittedDrag);
    }

    this.setCanvasDragPlaceholderPosition(null);
    this.lastSubmittedDrag = false;
    uiStore.endCanvasElementDrag();

    clearTextSelection();
  }

  handleDragWhenMouseIsOverAChildOfTheDropTarget(
    mouseX: number,
    mouseY: number,
    mouseOverNodeId: number,
    draggedNodeId: number,
    dropTargetNode: TreeNode,
    placeholderOnly = false
  ) {
    const stackingStyle = getStackingStyle(dropTargetNode);

    const insertBeforeOrAfter =
      shouldItBeInsertedBeforeOrAfter(
        mouseX,
        mouseY,
        mouseOverNodeId,
        stackingStyle
      ) === "before"
        ? "before"
        : "after";

    const nodesInDropTarget = dropTargetNode.children
      .map(node => node.id)
      .filter(id => id !== draggedNodeId);

    const dragPlaceholderIndex = dropTargetNode.children
      .map(node => node.id)
      .findIndex(id => id === mouseOverNodeId);

    let dropPlacementIndex = nodesInDropTarget.findIndex(
      id => id === mouseOverNodeId
    );

    if (insertBeforeOrAfter === "after") {
      dropPlacementIndex++; // for the placement of the dropped element, 0 inserts before the first node, 1 inserts after the first node
    }

    if (placeholderOnly === true) {
      this.setPlaceholder({
        dropTargetId: dropTargetNode.id,
        dragPlaceholderIndex: dragPlaceholderIndex,
        beforeOrAfter: insertBeforeOrAfter
      });

      this.setDropPosition(false);
    } else {
      this.setPlaceholder({
        dropTargetId: dropTargetNode.id,
        dragPlaceholderIndex: dragPlaceholderIndex,
        beforeOrAfter: insertBeforeOrAfter
      });

      this.setDropPosition({
        draggedElementId: draggedNodeId,
        droppedOnElementId: dropTargetNode.id,
        index: dropPlacementIndex
      });
    }
  }

  handleDragWhenMouseIsOverTheDropTargetButNotOverAChildElementOfTheDropTarget(
    mouseX: number,
    mouseY: number,
    _mouseOverNodeId: number,
    draggedNodeId: number,
    dropTargetNode: TreeNode
  ) {
    if (dropTargetNode.children.length === 0) {
      this.setPlaceholder({
        dropTargetId: dropTargetNode.id,
        dragPlaceholderIndex: 0,
        beforeOrAfter: "after"
      });

      this.setDropPosition({
        draggedElementId: draggedNodeId,
        droppedOnElementId: dropTargetNode.id,
        index: 0
      });
    } else {
      const rects = (
        dropTargetNode.children
          .map((node, i) => {
            return {
              positionAndSize: getTreeNodePositionAndSize(node, "absolute"), // we need to save the index to figure out which rect,
              index: i,
              id: node.id
            };
          })
          .filter(r => r.positionAndSize) as {
          index: number;
          positionAndSize: ElementPositionAndSize;
          id: number;
        }[]
      )
        .map(r => {
          return {
            min: {
              x: r.positionAndSize.x,
              y: r.positionAndSize.y
            },
            max: {
              x: r.positionAndSize.x + r.positionAndSize.width,
              y: r.positionAndSize.y + r.positionAndSize.height
            },
            nodeIndex: r.index,
            id: r.id
          };
        })
        .map(r => {
          return {
            ...r,
            distance: distance(r, { x: mouseX, y: mouseY })
          };
        });

      const nearestRect = rects.reduce((r1, r2) =>
        r1.distance < r2.distance ? r1 : r2
      );

      const xCenter = (nearestRect.min.x + nearestRect.max.x) / 2;
      const yCenter = (nearestRect.min.y + nearestRect.max.y) / 2;

      const leftOrRight = xCenter > mouseX ? "left" : "right";
      const aboveOrBelow = yCenter > mouseY ? "above" : "below";

      const stackingStyle = getStackingStyle(dropTargetNode);

      let insertBeforeOrAfter: "before" | "after";

      if (stackingStyle === "vertical") {
        insertBeforeOrAfter = aboveOrBelow === "above" ? "before" : "after";
      } else {
        insertBeforeOrAfter = leftOrRight === "left" ? "before" : "after";
      }

      if (nearestRect.id === draggedNodeId) {
        // if nearest node is self, just recompute the placeholder position.

        this.setPlaceholder({
          dropTargetId: dropTargetNode.id,
          dragPlaceholderIndex: nearestRect.nodeIndex,
          beforeOrAfter: insertBeforeOrAfter
        });

        this.setDropPosition(false);

        return;
      }

      const nodesInDropTargetIds = dropTargetNode.children
        .map(node => node.id)
        .filter(id => id !== draggedNodeId);

      let dropPlacementIndex = nodesInDropTargetIds.findIndex(
        id => id === nearestRect.id
      );

      if (insertBeforeOrAfter === "after") {
        dropPlacementIndex++;
      }

      this.setPlaceholder({
        dropTargetId: dropTargetNode.id,
        dragPlaceholderIndex: nearestRect.nodeIndex,
        beforeOrAfter: insertBeforeOrAfter
      });

      this.setDropPosition({
        draggedElementId: draggedNodeId,
        droppedOnElementId: dropTargetNode.id,
        index: dropPlacementIndex
      });
    }
  }

  setPlaceholder(placeholderPosition: PlaceholderPosition) {
    if (
      isThisNodeAllowedInsideThatNode(
        documentStore.document.tree,
        this.node,
        placeholderPosition.dropTargetId
      )
    ) {
      this.movePlaceholder(
        placeholderPosition.dropTargetId,
        placeholderPosition.dragPlaceholderIndex,
        placeholderPosition.beforeOrAfter
      );
    }
  }

  setDropPosition(dropPosition: DragPayload | false) {
    if (dropPosition === false) {
      this.lastSubmittedDrag = false;
      return;
    }

    if (
      isThisNodeAllowedInsideThatNode(
        documentStore.document.tree,
        this.node,
        dropPosition.droppedOnElementId
      )
    ) {
      this.lastSubmittedDrag = dropPosition;
    }
  }

  movePlaceholder(
    dropTargetId: number,
    dragPlaceholderIndex: number,
    beforeOrAfter: "before" | "after"
  ) {
    let onlyShowTargetOutline = false;

    /* hide if placeholder is on the dragged node */
    const dropTarget = findNode(documentStore.document.tree, dropTargetId);
    if (dropTarget) {
      const maybeChildWherePlaceholderIs =
        dropTarget.children[dragPlaceholderIndex];
      const maybeChildBeforeWherePlaceholderIs =
        dropTarget.children[dragPlaceholderIndex - 1];
      const maybeChildAFterWherePlaceholderIs =
        dropTarget.children[dragPlaceholderIndex + 1];

      if (maybeChildWherePlaceholderIs) {
        if (maybeChildWherePlaceholderIs.id === this.node.id) {
          onlyShowTargetOutline = true;
          // return;
        }
      }

      if (maybeChildBeforeWherePlaceholderIs) {
        if (
          maybeChildBeforeWherePlaceholderIs.id === this.node.id &&
          beforeOrAfter === "before"
        ) {
          onlyShowTargetOutline = true;
          // return;
        }
      }

      if (maybeChildAFterWherePlaceholderIs) {
        if (
          maybeChildAFterWherePlaceholderIs.id === this.node.id &&
          beforeOrAfter === "after"
        ) {
          onlyShowTargetOutline = true;
          // return;
        }
      }
    }
    /* ---------- */

    /* only move if it is materially different than last position */
    if (uiStore.canvasDragPlaceholderPosition) {
      const movedForwards = () =>
        dragPlaceholderIndex ===
        (uiStore.canvasDragPlaceholderPosition as CanvasDragPlaceholderPosition)
          .nodeIndex +
          1;

      const movedBackwards = () =>
        dragPlaceholderIndex ===
        (uiStore.canvasDragPlaceholderPosition as CanvasDragPlaceholderPosition)
          .nodeIndex -
          1;

      if (uiStore.canvasDragPlaceholderPosition.parentId === dropTargetId) {
        if (
          movedForwards() &&
          uiStore.canvasDragPlaceholderPosition.position === "after" &&
          beforeOrAfter === "before"
        ) {
          return;
        }

        if (
          movedBackwards() &&
          uiStore.canvasDragPlaceholderPosition.position === "before" &&
          beforeOrAfter === "after"
        ) {
          return;
        }
      }
    }
    /* ------ */

    this.setCanvasDragPlaceholderPosition({
      parentId: dropTargetId,
      nodeIndex: dragPlaceholderIndex,
      position: beforeOrAfter,
      onlyShowTargetOutline: onlyShowTargetOutline
    });
  }
}
